import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/base/ScrollToTop";
import SEOHead from "@/components/base/SEOHead";
import Breadcrumb from "@/components/base/Breadcrumb";
import ArticleCard from "@/components/base/ArticleCard";
import { articles } from "@/mocks/articles";
import { categories } from "@/mocks/categories";

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <div className="min-h-screen bg-background-50 pt-24 pb-20">
          <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6 text-center">
            <h1 className="text-2xl font-bold text-foreground-900 mb-4">文章未找到</h1>
            <Link
              to="/"
              className="text-primary-500 hover:text-primary-600 transition-colors duration-200 cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const category = categories.find((c) => c.id === article.categoryId);
  const relatedArticles = articles
    .filter((a) => a.id !== article.id && a.categoryId === article.categoryId)
    .slice(0, 3);

  if (relatedArticles.length < 3) {
    const otherArticles = articles
      .filter((a) => a.id !== article.id && a.categoryId !== article.categoryId)
      .slice(0, 3 - relatedArticles.length);
    relatedArticles.push(...otherArticles);
  }

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    datePublished: article.date,
    dateModified: article.date,
    author: {
      "@type": "Organization",
      name: article.author,
    },
    publisher: {
      "@type": "Organization",
      name: "91精选",
      url: "https://91jingxuan.com",
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://91jingxuan.com/article/${article.id}`,
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "首页",
        item: "https://91jingxuan.com",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: category?.name || "分类",
        item: `https://91jingxuan.com/category/${category?.slug || ""}`,
      },
      {
        "@type": "ListItem",
        position: 3,
        name: article.title,
      },
    ],
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: `这篇文章是关于什么的？`,
        acceptedAnswer: {
          "@type": "Answer",
          text: `本文主题为「${article.title}」，属于${category?.name || "精选内容"}分类，由91精选编辑团队整理发布。`,
        },
      },
      {
        "@type": "Question",
        name: `如何获取更多${category?.name || "精选"}内容？`,
        acceptedAnswer: {
          "@type": "Answer",
          text: `您可以访问91精选的${category?.name || "相关"}分类页面，或订阅我们的精选推送，获取更多优质内容推荐。`,
        },
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={`${article.title} - 91精选`}
        description={article.excerpt}
        keywords={`${article.tags.join(",")},91精选,${category?.name || ""},精选内容`}
        canonicalUrl={`/article/${article.id}`}
        ogType="article"
        ogImage={article.image}
        publishedTime={article.date}
        modifiedTime={article.date}
        tags={article.tags}
        schema={{ ...articleSchema, ...breadcrumbSchema, ...faqSchema }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-24 pb-20">
          <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="max-w-[800px] mx-auto">
              <Breadcrumb
                items={[
                  { label: "首页", href: "/" },
                  { label: category?.name || "分类", href: `/category/${category?.slug || ""}` },
                  { label: article.title },
                ]}
              />

              <article className="mt-8">
                <div className="mb-6">
                  <div className="flex flex-wrap items-center gap-3 mb-4">
                    <span className="inline-flex items-center gap-1 px-3 py-1 text-xs font-medium bg-primary-100 text-primary-700 rounded-full whitespace-nowrap">
                      <i className={`${category?.icon || "ri-file-text-line"} text-xs`}></i>
                      {category?.name || "精选"}
                    </span>
                    {article.tags.map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center px-2.5 py-1 text-xs bg-background-100 text-foreground-600 rounded-full whitespace-nowrap"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  <h1 className="text-2xl md:text-4xl font-bold text-foreground-900 mb-4 leading-tight">
                    {article.title}
                  </h1>

                  <div className="flex items-center gap-4 text-sm text-foreground-500">
                    <span className="flex items-center gap-1">
                      <i className="ri-user-line"></i>
                      {article.author}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-calendar-line"></i>
                      {article.date}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-time-line"></i>
                      {article.readTime} 分钟阅读
                    </span>
                  </div>
                </div>

                <div className="w-full h-[400px] md:h-[480px] rounded-lg overflow-hidden mb-10">
                  <img
                    src={article.image}
                    alt={article.title}
                    title={`${article.title} - 91精选`}
                    className="w-full h-full object-cover"
                  />
                </div>

                <div
                  className="prose prose-lg max-w-none text-foreground-800 leading-relaxed article-content"
                  dangerouslySetInnerHTML={{ __html: article.content }}
                />
              </article>

              <div className="mt-16 pt-10 border-t border-background-200/70">
                <h2 className="text-xl font-bold text-foreground-900 mb-6">
                  相关推荐
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {relatedArticles.map((related) => (
                    <ArticleCard key={related.id} article={related} />
                  ))}
                </div>
              </div>

              <div className="mt-8 text-center">
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-background-100 border border-background-300/60 text-foreground-800 rounded-full text-sm font-medium hover:bg-background-200/70 transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-home-line"></i>
                  返回首页
                </Link>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}